import type { Router } from '@remix-run/router';
import { useEffect, useRef, useState } from 'react';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { CRS_PATH, PATH_DASHBOARD } from 'src/routes/paths';
import { isIcomxBlockedPath } from './useIcomxAvailability';
import {
  ICOMX_LOGOUT_EVENT,
  ICOMX_LOGOUT_STORAGE_KEY,
  isIcomxSessionLoggedOut,
  notifyIcomxLogout,
  readIcomxSession,
} from './session';

export function isIcomxSessionPath(pathname: string) {
  return !isIcomxBlockedPath(pathname) && [
    PATH_DASHBOARD.root, CRS_PATH.provider.root, '/settings', '/_debug',
  ].some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export default function useIcomxSession(router: Router) {
  const { isInitialized, isAuthenticated } = useAuth();
  const [pathname, setPathname] = useState(router.state.location.pathname);
  const [, refresh] = useState(0);
  const [loggedOutKey, setLoggedOutKey] = useState<string | null>(null);
  const wasAuthenticated = useRef(isAuthenticated);
  const session = readIcomxSession();

  useEffect(() => {
    setPathname(router.state.location.pathname);
    return router.subscribe(({ location }) => setPathname(location.pathname));
  }, [router]);

  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) notifyIcomxLogout();
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    let previous = '';
    let expiry: ReturnType<typeof setTimeout>;
    const check = () => {
      const current = readIcomxSession();
      const next = JSON.stringify([current, current && isIcomxSessionLoggedOut(current)]);
      if (next !== previous) {
        previous = next;
        refresh((value) => value + 1);
        clearTimeout(expiry);
        if (current) expiry = setTimeout(check, Math.min(current.expiresAt - Date.now(), 2147483647));
      }
    };
    const logout = () => {
      setLoggedOutKey(readIcomxSession()?.logoutKey ?? null);
      check();
    };
    const storage = (event: StorageEvent) => {
      if (event.key === ICOMX_LOGOUT_STORAGE_KEY || event.key === null) check();
    };
    // Cookies have no cross-browser change event. Poll only this integration and
    // schedule expiry exactly; focus/visibility checks cover background throttling.
    check();
    const poll = setInterval(check, 1000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('storage', storage);
    window.addEventListener(ICOMX_LOGOUT_EVENT, logout);
    return () => {
      clearTimeout(expiry);
      clearInterval(poll);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('storage', storage);
      window.removeEventListener(ICOMX_LOGOUT_EVENT, logout);
    };
  }, []);

  return isInitialized && session && isIcomxSessionPath(pathname) &&
    session.logoutKey !== loggedOutKey && !isIcomxSessionLoggedOut(session)
    ? session.key : null;
}
